import React, { useState, useEffect, useRef } from 'react';
import * as turf from '@turf/turf';
import moment from 'moment-timezone';
import playBtn from '../../../../assets/icons/play-button-icon.png';
import pauseBtn from '../../../../assets/icons/pause-button-icon.png';
import fastForwardBtn from '../../../../assets/icons/fast-forward-button-icon.png';
import NearbyInfrastructure from './NearbyInfrastructure';
import refreshIcon from '../../../../assets/icons/refresh-icon.png';
import '../FirePopUp.scss';
import mapboxgl from 'mapbox-gl';
import axios from 'axios';
import nearbyInfrastructureIcon from '../../../../assets/icons/nearby-infrastructure-icon.png';
import Powerlines from './Powerlines';
import centroid from '@turf/centroid';
import Spinner from '../../../Spinner';
import video2 from '../../../../assets/icons/sim-video-2.mov';

export default function FireSimulation({ map, setIs3D, getToken, latitude, longitude, fire }) {
  const [loading, setLoading]           = useState(true)
  const [simulation, setSimulation]     = useState(null)
  const [isViewing, setIsViewing]       = useState(false)
  const [currentStep, setCurrentStep]   = useState(0)
  const [isPlaying, setIsPlaying]       = useState(true)
  const [stage, setStage]               = useState('Nearby Infrastructure')
  const [nearbyInfra, setNearbyInfra]   = useState(null)
  const [powerlines, setPowerlines]     = useState([])

  const popupRef = useRef(null)
  const pollingIntervalRef = useRef(null)
  const timeSeries = simulation?.simulation || []

  // Validation function to check if simulation data is sufficient
  const isSimulationDataValid = (data) => {
    if (!data || data.length === 0) return false;
    
    // Check if we have more than one step (for meaningful animation)
    if (data.length <= 1) return false;
    
    // Check if we have meaningful fire spread by looking at the largest polygon in each step
    const minArea = 0.00001; // Much smaller threshold - just to filter out truly tiny polygons
    let hasSignificantSpread = false;
    
    for (const record of data) {
      if (!record.polygons_wkt || record.polygons_wkt.length === 0) return false;
      
      // Find the largest polygon in this step
      let maxAreaInStep = 0;
      for (const wkt of record.polygons_wkt) {
        try {
          const coords = wkt.slice(9, -2).split(',').map(p => p.trim().split(' ').map(Number));
          const polygon = turf.polygon([coords]);
          const area = turf.area(polygon);
          
          // Convert from square meters to square degrees (rough approximation)
          const areaInSquareDegrees = area / (111000 * 111000); // 1 degree ≈ 111km
          
          if (areaInSquareDegrees > maxAreaInStep) {
            maxAreaInStep = areaInSquareDegrees;
          }
        } catch (error) {
          console.warn('Error validating polygon area:', error);
          return false;
        }
      }
      
      // If any step has a reasonably sized polygon, consider it valid
      if (maxAreaInStep > minArea) {
        hasSignificantSpread = true;
      }
    }
    
    return hasSignificantSpread;
  };

  const hasValidSimulationData = isSimulationDataValid(timeSeries);

      
  const makeInfraPopupHTML = ({ type, underground, height, distance }) => {
    const heightFt = height != null
      ? (height * 3.28084).toFixed(2) + ' ft'
      : 'Not Available';
  
    return `
      <div>
        <div class="alert-popup-title">
          ${(type || '').toUpperCase() || 'Not Available'}
        </div>
  
        <div class="alert-popup-row">
          <div class="alert-popup-row-name">Height:</div>
          <div class="alert-popup-row-value">
            ${heightFt}
          </div>
        </div>
  
        <div class="alert-popup-row">
          <div class="alert-popup-row-name">Underground:</div>
          <div class="alert-popup-row-value">
            ${(underground || '').toUpperCase() || 'Not Available'}
          </div>
        </div>
  
        <div class="alert-popup-row">
          <div class="alert-popup-row-name">Distance from Fire:</div>
          <div class="alert-popup-row-value">
            ${distance.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })} miles
          </div>
        </div>
      </div>
    `;
  };  

  const makePowerlinesPopupHTML = ({ name, type, status, distance }) => `
      <div>
        <div class="alert-popup-title">${name}</div>
        <div class="alert-popup-row">
          <div class="alert-popup-row-name">Type:</div>
          <div class="alert-popup-row-value">${(type||'').toUpperCase() || 'Not Available'}</div>
        </div>
        <div class="alert-popup-row">
          <div class="alert-popup-row-name">Status:</div>
          <div class="alert-popup-row-value">${status || 'Not Available'}</div>
        </div>
        <div class="alert-popup-row">
          <div class="alert-popup-row-name">Distance from Fire:</div>
          <div class="alert-popup-row-value">${distance.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} miles</div>
        </div>
      </div>
  `

  const formatAddress = (address) => {
      if (address) {
        const { street, housenumber } = address;
      
        if (street && housenumber) {
          return `${street} ${housenumber}`;
        }
      
        if (street) {
          return street;
        }
      
        return 'Not Available';
      } else {
        return 'Not Available';
      }
  }

  const parseWKTLine = (wkt) => {
    return wkt
      .slice('LINESTRING('.length, -1)
      .split(',')
      .map(pair => {
        const [lon, lat] = pair.trim().split(' ').map(Number)
        return [lon, lat]
      })
  }
  
  const calculateInfraDistance = (coord1, coord2) => {
      const R = 6371;
      const milesConversionFactor = 0.621371;
      const lat1 = coord1[1];
      const lon1 = coord1[0];
      const lat2 = coord2[1];
      const lon2 = coord2[0];
  
      const dLat = (lat2 - lat1) * (Math.PI / 180);
      const dLon = (lon2 - lon1) * (Math.PI / 180);
  
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distanceInKilometers = R * c;
      const distanceInMiles = distanceInKilometers * milesConversionFactor;
  
      return distanceInMiles;
  };

  const calculatePowerlineDistance = (pointCoord, powerline) => {
    const pt = turf.point(pointCoord)
    const line = turf.lineString(parseWKTLine(powerline.wkt))
  
    return turf.pointToLineDistance(pt, line, { units: 'miles' })
  }
  
  const showInfraPopup = (lngLat, props) => {
      if (popupRef.current) popupRef.current.remove()
  
      popupRef.current = new mapboxgl.Popup({
        offset: 25,
        maxWidth: '300px',
        className: 'popup'
      })
        .setLngLat(lngLat)
        .setHTML(makeInfraPopupHTML(props))
        .addTo(map.current)
  }

  const showPowerlinesPopup = (lngLat, props) => {
    if (popupRef.current) popupRef.current.remove()

    popupRef.current = new mapboxgl.Popup({
      offset: 25,
      maxWidth: '300px',
      className: 'popup'
    })
      .setLngLat(lngLat)
      .setHTML(makePowerlinesPopupHTML(props))
      .addTo(map.current)
  }

  const getSimulation = async () => {
    setLoading(true)
    try {
      const token = await getToken()
      const { data } = await axios.get(
        `https://firewatch-api.space-eyes.com/fires/simulations?fire_id=${fire.fire_id}`,
        { headers: { Authorization: `Bearer ${token}` }}
      )
      const currentSimulation = data.simulations?.[0] ?? null
      setSimulation(currentSimulation)

      console.log(data)
      
      // If simulation is pending, start polling
      if (currentSimulation?.status === 'pending') {
        pollSimulationStatus()
      } else {
        setLoading(false)
      }
    } catch (err) {
      console.error('error fetching simulation', err)
      setSimulation(null)
      setLoading(false)
    }
  }
  
  const requestSimulation = async () => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    
    setLoading(true)
    try {
      const token = await getToken()
      const { data } = await axios.post(
        `https://firewatch-api.space-eyes.com/fires/simulations`,
        { fire_id: fire.fire_id },
        { headers: { Authorization: `Bearer ${token}` }}
      )
      setSimulation(data.simulations?.[0] ?? null)
      
      // If simulation is pending, start polling until it's complete or failed
      if (data.simulations?.[0]?.status === 'pending') {
        pollSimulationStatus()
      } else {
        setLoading(false)
      }
    } catch (err) {
      console.error('error requesting simulation', err)
      setLoading(false)
    }
  }

  const pollSimulationStatus = async () => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
    }
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const token = await getToken()
        const { data } = await axios.get(
          `https://firewatch-api.space-eyes.com/fires/simulations?fire_id=${fire.fire_id}`,
          { headers: { Authorization: `Bearer ${token}` }}
        )
        const currentSimulation = data.simulations?.[0] ?? null
        setSimulation(currentSimulation)

        console.log(data)
        
        // Stop polling if simulation is complete or failed
        if (currentSimulation?.status === 'complete' || currentSimulation?.status === 'failed') {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
          setLoading(false)
        }
      } catch (err) {
        console.error('error polling simulation status', err)
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
        setLoading(false)
      }
    }, 5000) // Poll every 5 seconds
  }

  const plotNearbyInfrastructure = (map) => {
    try {
      function parseMultiPolygonWKT(polygons) {
        const coords = polygons.map(poly => {
          const ringText = poly
            .replace(/^\s*POLYGON\s*\(\(/i, '')
            .replace(/\)\)\s*$/, '');
          const pairs = ringText.split(/\s*,\s*/);
          const ring = pairs.map(pair =>
            pair.trim().split(/\s+/).map(Number)
          );
          if (
            ring.length &&
            (ring[0][0] !== ring[ring.length-1][0] ||
             ring[0][1] !== ring[ring.length-1][1])
          ) {
            ring.push(ring[0]);
          }
          return ring;
        });
      
        return {
          type: 'MultiPolygon',
          coordinates: coords.map(ring => [ring])
        };
      }
      
      const lastStep = timeSeries.length > 0 ? timeSeries[timeSeries.length - 1].polygons_wkt : {};
      
      const multiPoly = parseMultiPolygonWKT(lastStep);
      
      const rawBuildings = map.current
        .querySourceFeatures('composite', { sourceLayer: 'building' })
        .filter(feat =>
          turf.booleanIntersects(multiPoly, feat)
      );

      if (rawBuildings.length === 0) {
        setNearbyInfra([]);
      } else {
        setNearbyInfra(rawBuildings.filter((item, index, self) =>
          index === self.findIndex(other => other.id === item.id)
        ));
      }
      
      // Clean up existing layers and source with error handling
      try {
        const nearbyInfrastructureLayers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-nearby-infrastructure`));
        if (nearbyInfrastructureLayers.length > 0) {
          nearbyInfrastructureLayers.forEach((layer) => {
            try {
              if (map.current.getLayer(layer.id)) {
                map.current.removeLayer(layer.id);
              }
            } catch (e) {
              console.warn(`Error removing layer ${layer.id}:`, e);
            }
          });
        }
    
        // Clean up existing sources (including dynamic ones)
        try {
          const allSources = Object.keys(map.current.getStyle().sources);
          allSources.forEach(sourceId => {
            if (sourceId.startsWith('Source-nearby-infrastructure')) {
              if (map.current.getSource(sourceId)) {
                map.current.removeSource(sourceId);
              }
            }
          });
        } catch (e) {
          console.warn('Error removing sources:', e);
        }
    
        // Clean up existing image
        try {
          if (map.current.hasImage('Image-nearby-infrastructure-Icon')) {
            map.current.removeImage('Image-nearby-infrastructure-Icon');
          }
        } catch (e) {
          console.warn('Error removing image:', e);
        }
      } catch (e) {
        console.warn('Error during cleanup:', e);
      }
  
      map.current.loadImage(nearbyInfrastructureIcon, (error, image) => {
        if (error) throw error;
        
        // Check if image already exists before adding
        if (!map.current.hasImage('Image-nearby-infrastructure-Icon')) {
          map.current.addImage('Image-nearby-infrastructure-Icon', image);
        }
  
        const sourceId = `Source-nearby-infrastructure-${Date.now()}`;
        map.current.addSource(sourceId, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: rawBuildings
          }
        });
        
        // Check if layer already exists before adding
        if (!map.current.getLayer("Layer-nearby-infrastructure")) {
          map.current.addLayer({
            id: "Layer-nearby-infrastructure",
            type: "symbol",
            source: sourceId,
            minzoom: 9,
            layout: {
              'icon-image': 'Image-nearby-infrastructure-Icon',
              'icon-size': 0.15,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true
            }
          });
        }
  
        map.current.on('mouseenter', 'Layer-nearby-infrastructure', () => {
          map.current.getCanvas().style.cursor = 'pointer';
        });
        map.current.on('mouseleave', 'Layer-nearby-infrastructure', () => {
          map.current.getCanvas().style.cursor = '';
        });
      });
    } catch (exception) {
      console.log('error getting nearby infra')
    }
  }

  useEffect(() => { 
    getSimulation();
  }, [])

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isViewing || !simulation || timeSeries.length === 0) return

    setCurrentStep(0)

    const features = timeSeries.flatMap(r =>
      r.polygons_wkt.map(wkt => {
        const coords = wkt
          .slice(9, -2)
          .split(',')
          .map(p => p.trim().split(' ').map(Number))
        return turf.polygon([coords])
      })
    )
    const bbox = turf.bbox(turf.featureCollection(features))
    map.current.fitBounds(bbox, { padding: 80, maxZoom: 19, pitch: 65 })
    setIs3D(true)

    // plot infra & powerlines after map idle (only if we have valid simulation data)
    if (hasValidSimulationData) {
      map.current.once('idle', () => {
        plotNearbyInfrastructure(map)
        fetchPowerlines()
      })
    }
  }, [isViewing, simulation])

  // 4) auto‐play animation
  useEffect(() => {
    if (!isViewing || !isPlaying) return
    const id = setInterval(() => {
      setCurrentStep(prev =>
        prev + 1 < timeSeries.length ? prev + 1 : 0
      )
    }, 1000)
    return () => clearInterval(id)
  }, [isViewing, isPlaying, timeSeries.length])

  const fetchPowerlines = () => {
    console.log('powerlines api called');
    const lastStep = timeSeries.length > 0 ? timeSeries[timeSeries.length - 1].polygons_wkt: {};
    function toMultiPolygon(polygons) {
      const rings = polygons.map(poly => {
        const m = poly.match(/^\s*POLYGON\s*(\(\(.*\)\))\s*$/i);
        if (!m) {
          throw new Error(`Invalid POLYGON WKT: ${poly}`);
        }
        return m[1];
      });
      
      return `MULTIPOLYGON(${rings.join(',')})`;
    }
    const spread = toMultiPolygon(lastStep);
    console.log(spread)
    getToken().then((token) => {
      axios.post(`https://firewatch-api.space-eyes.com/powerlines`, {
        // spread: 'POLYGON((-118.79951632857632 34.47376938513672,-118.83403905656762 34.02103685309835,-117.09790297027374 32.54451350646224,-116.5737765330319 33.93523183080693,-117.62812445466434 34.55424298357471,-118.79951632857632 34.47376938513672))', 
        spread: spread
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
      ).then((response) => {
          console.log('powerlines: ', response.data.powerlines)
          if (response.data.powerlines[0].message) {
            setPowerlines([]);
            return;
          }

          setPowerlines(response.data.powerlines);

          const powerlineLayers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-powerlines`));

          if (powerlineLayers.length > 0) {
            powerlineLayers.forEach((layer) => map.current.removeLayer(layer.id))
          }
  
          if (map.current.getStyle().sources[`Source-powerlines`]) {
              map.current.removeSource(`Source-powerlines`);
          }

          const parseWKTLine2 = (wkt) => {
            const coords = wkt
              .slice(11, -1)       
              .split(',')
              .map(pair => {
                const [lon, lat] = pair.trim().split(' ').map(Number)
                return [lon, lat]
              })
            return { type: 'LineString', coordinates: coords }
          }

          const features = response.data.powerlines.map(pl => ({
            type: 'Feature',
            geometry: parseWKTLine2(pl.wkt),
            properties: {
              id:         pl.POWERLINE_ID,
              type:       pl.TYPE,
              status:     pl.STATUS,
              owner:      pl.OWNER,
              voltage:    pl.VOLTAGE
            }
          }))
    
          const geojson = {
            type: 'FeatureCollection',
            features
          }

          map.current.addSource("Source-powerlines", {
            type: "geojson",
            data: geojson
          });

          // Check if layer already exists before adding
          if (!map.current.getLayer('Layer-powerlines')) {
            map.current.addLayer({
              id: 'Layer-powerlines',
              type: 'line',
              source: 'Source-powerlines',
            layout: {
              'line-join': 'round',
              'line-cap':  'round'
            },
            paint: {
              'line-color': 'rgb(0, 229, 255)',
              'line-width':  4
            }
          });
          }
            
          map.current.on('mouseenter', 'Layer-powerlines', () => {
            map.current.getCanvas().style.cursor = 'pointer';
          });
          map.current.on('mouseleave', 'Layer-powerlines', () => {
            map.current.getCanvas().style.cursor = '';
          });
      }).catch((error) => {
          console.log('error getting nearby infrastructure', error);
      })
    }).catch((err) => {
        console.log('error getting token', err);
    });
}
    
useEffect(() => {
      const handler = e => {
        const feat = e.features[0]
        const props = {
          name: feat.properties.name,
          type: feat.properties.type,
          underground: feat.properties.underground,
          height:  feat.properties.height,
          distance: calculateInfraDistance(
            [latitude, longitude],
            [centroid(feat).geometry.coordinates[1], centroid(feat).geometry.coordinates[0]]
          )
        }
        showInfraPopup(centroid(feat).geometry.coordinates, props)
      }

      map.current.on('click', 'Layer-nearby-infrastructure', handler)
      map.current.on('mouseenter', 'Layer-nearby-infrastructure', () => {
        map.current.getCanvas().style.cursor = 'pointer'
      })
      map.current.on('mouseleave', 'Layer-nearby-infrastructure', () => {
        map.current.getCanvas().style.cursor = ''
      })
    
      return () => {
        map.current.off('click', 'Layer-nearby-infrastructure', handler)
        map.current.off('mouseenter', 'Layer-nearby-infrastructure')
        map.current.off('mouseleave', 'Layer-nearby-infrastructure')
      }
}, []);

useEffect(() => {
  const handler = e => {
    const feat = e.features[0]
    const props = {
      name: feat.SUB_1 ? feat.SUB_1 : feat.SUB_2 ? feat.SUB_2 : feat.id,
      type: feat.properties.TYPE,
      status: feat.properties.STATUS,
      distance: calculatePowerlineDistance(
        [latitude, longitude],
        feat
      )
    }
    showPowerlinesPopup(parseWKTLine(feat.wkt)[0], props)
  }

  map.current.on('click', 'Layer-Powerlines', handler)
  map.current.on('mouseenter', 'Layer-Powerlines', () => {
    map.current.getCanvas().style.cursor = 'pointer'
  })
  map.current.on('mouseleave', 'Layer-Powerlines', () => {
    map.current.getCanvas().style.cursor = ''
  })

  return () => {
    map.current.off('click', 'Layer-Powerlines', handler)
    map.current.off('mouseenter', 'Layer-Powerlines')
    map.current.off('mouseleave', 'Layer-Powerlines')
  }
}, []);
  

const WKTtoRings = (wkt) => {
  const rings = wkt.slice(9).slice(0, -2).split(',').map((coordinates) => {
      return coordinates.split(' ').filter((element) => element !== '').map((element) => parseFloat(element));
  });
      return rings;
};

const formatIsoToLocal = (isoString) => {
    return moment
    .tz(isoString, "UTC")          
    .tz("America/New_York") 
    .format("YYYY-MM-DD h:mm A");
}

const record = timeSeries.find(r => r.step === currentStep) || { step: 0, time: null, polygons_wkt: []}

const fc = {
  type: 'FeatureCollection',
  features: record.polygons_wkt.map(wkt => ({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [WKTtoRings(wkt)]
    },
    properties: { step: record.step, time: record.time }
  }))
};

useEffect(() => {
  if (isViewing) {
    const fiveMilelayers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-fire-1mi`));

    if (fiveMilelayers.length > 0) {
        fiveMilelayers.forEach((layer) => map.current.removeLayer(layer.id))
    }

    if (map.current.getStyle().sources[`Source-fire-1mi`]) {
        map.current.removeSource(`Source-fire-1mi`);
    }

    const oneMilelayers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-fire-5mi`));

    if (oneMilelayers.length > 0) {
        oneMilelayers.forEach((layer) => map.current.removeLayer(layer.id))
    }

    if (map.current.getStyle().sources[`Source-fire-5mi`]) {
        map.current.removeSource(`Source-fire-5mi`);
    }

    const windConeLayers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-fire-wind-cone`));

    if (windConeLayers.length > 0) {
        windConeLayers.forEach((layer) => map.current.removeLayer(layer.id))
    }

    if (map.current.getStyle().sources[`Source-fire-wind-cone`]) {
        map.current.removeSource(`Source-fire-wind-cone`);
    }

    const bbox = turf.bbox(fc);
    map.current.fitBounds(bbox, { padding: 80, maxZoom: 17, pitch: 65 });
    setIs3D(true);

    if (hasValidSimulationData) {
      map.current.once('idle', () => {
        plotNearbyInfrastructure(map)
      });
    }
  }
  return () => {
      const simulationLayers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-fire-simulation`));

      if (simulationLayers.length > 0) {
          simulationLayers.forEach((layer) => map.current.removeLayer(layer.id))
      }

      if (map.current.getStyle().sources[`Source-fire-simulation`]) {
          map.current.removeSource(`Source-fire-simulation`);
      }

      // Clean up existing layers
      const nearbyInfrastructureLayers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-nearby-infrastructure`));
      if (nearbyInfrastructureLayers.length > 0) {
        nearbyInfrastructureLayers.forEach((layer) => {
          try {
            if (map.current.getLayer(layer.id)) {
              map.current.removeLayer(layer.id);
            }
          } catch (e) {
            console.warn(`Error removing layer ${layer.id}:`, e);
          }
        });
      }

      // Clean up existing sources (including dynamic ones)
      try {
        const allSources = Object.keys(map.current.getStyle().sources);
        allSources.forEach(sourceId => {
          if (sourceId.startsWith('Source-nearby-infrastructure')) {
            try {
              if (map.current.getSource(sourceId)) {
                map.current.removeSource(sourceId);
              }
            } catch (e) {
              console.warn(`Error removing source ${sourceId}:`, e);
            }
          }
        });
      } catch (e) {
        console.warn('Error during source cleanup:', e);
      }

      const powerlineLayers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-powerlines`));

      if (powerlineLayers.length > 0) {
        powerlineLayers.forEach((layer) => map.current.removeLayer(layer.id))
      }

      if (map.current.getStyle().sources[`Source-powerlines`]) {
          map.current.removeSource(`Source-powerlines`);
      }
  }
}, [isViewing]);

useEffect(() => {
  if (!map.current || !map.current.isStyleLoaded() || !isViewing) return;
  
  // Don't plot simulation polygons if data is insufficient
  if (!hasValidSimulationData) return;

  const sourceId = 'Source-fire-simulation';
  const fillLayer = 'Layer-fire-simulation-fill';
  const outlineLayer = 'Layer-fire-simulation-current-outline';

  const records = timeSeries
    .filter(r => r.step <= currentStep)
    .sort((a,b) => b.step - a.step);

  const allFeatures = records.flatMap(record =>
    record.polygons_wkt.map(wkt => ({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [ WKTtoRings(wkt) ]
      },
      properties: {
        step: record.step,
        time: record.time
      }
    }))
  );

  const jitterify = (features, distance = 0.0003) =>
    features.map(feat => {
      const coords = feat.geometry.coordinates[0].map(([lon, lat]) => [
        lon + (Math.random()*2-1)*distance,
        lat + (Math.random()*2-1)*distance
      ]);
      coords.push(coords[0]);
      return {
        ...feat,
        geometry: { ...feat.geometry, coordinates: [coords] }
      };
    });
  const jagged = jitterify(allFeatures);

  const maxStep = timeSeries.length - 1;
  
  // Ensure we have valid steps for interpolation
  if (maxStep <= 0) return;
  
  // Create color ramp with guaranteed ascending values
  const colorRamp = maxStep === 1 
    ? ['get', 'step'] === 0 ? 'rgb(115, 0, 0)' : 'rgb(255, 162, 0)'
    : [
        'interpolate', ['linear'], ['get','step'],
        0,       'rgb(115, 0, 0)',
        Math.max(1, Math.floor(maxStep * 0.25)),      'rgb(154, 0, 0)',
        Math.max(2, Math.floor(maxStep * 0.5)),       'rgb(192, 0, 0)',
        Math.max(3, Math.floor(maxStep * 0.75)),      'rgb(214, 0, 0)',
        maxStep, 'rgb(255, 162, 0)'
      ];

  // Remove existing layers first to prevent duplicates
  try {
    if (map.current.getLayer(fillLayer)) {
      map.current.removeLayer(fillLayer);
    }
    if (map.current.getLayer(outlineLayer)) {
      map.current.removeLayer(outlineLayer);
    }
  } catch (e) {
    console.warn('Error removing existing layers:', e);
  }

  if (!map.current.getSource(sourceId)) {
    map.current.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: jagged }
    });
  } else {
    map.current.getSource(sourceId).setData({
      type: 'FeatureCollection',
      features: jagged
    });
  }

  // Add layers only if they don't exist
  if (!map.current.getLayer(fillLayer)) {
    map.current.addLayer({
      id: fillLayer,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': colorRamp,
        'fill-opacity': 1
      }
    });
  }

  // Set filter for outline layer if it exists
  if (map.current.getLayer(outlineLayer)) {
    map.current.setFilter(
      outlineLayer,
      ['==', ['get','step'], currentStep]
    );
  }
}, [isViewing, currentStep, map, timeSeries, hasValidSimulationData]);

useEffect(() => {
  if (!isPlaying) return;

  const interval = setInterval(() => {
    setCurrentStep(prev =>
      prev + 1 < timeSeries.length ? prev + 1 : 0
    );
  }, 1000);

  return () => clearInterval(interval);
}, [isPlaying, timeSeries.length]);

if (loading) {
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '2rem',
      textAlign: 'center',
      color: '#666'
    }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
        {simulation?.status === 'pending' ? 'Processing AI Fire Spread Simulation' : 'Loading AI Fire Spread Simulation'}
      </div>
      <div style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
        {simulation?.status === 'pending' 
          ? 'Our AI is analyzing weather conditions, terrain, fire behavior, and more to generate your simulation. This may take a few minutes.' 
          : 'Fetching simulation data...'}
      </div>
      <Spinner />
    </div>
  )
}

if (!isViewing) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '0.5rem', marginTop: '0.3rem' }}>
    <div style={{ fontSize: '2rem' , color: 'black' }}>AI Fire Spread Simulation</div>
    <div style={{ fontSize: '0.8rem' , color: 'black' }}>Our AI Fire Spread Simulation harnesses a custom-trained artificial intelligence model to accurately predict the evolving path of a wildfire in real time. By intelligently processing dynamic weather conditions—including wind speed and direction, humidity, and temperature—alongside vegetation types, terrain slope, fuel moisture, and historical fire behavior, the model generates highly detailed forecasts of potential spread patterns.</div>
     {!simulation ? (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '1.5rem',
        textAlign: 'center',
        color: '#666',
        backgroundColor: 'rgba(229, 229, 229, 0.7)',
        borderRadius: '8px',
        border: '1px solid #ddd',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.3rem', color: '#333' }}>
          What to Expect
        </div>
        <div style={{ fontSize: '0.85rem', lineHeight: '1.4', marginBottom: '0.5rem' }}>
          Our AI will analyze current weather conditions, terrain features, vegetation types, historical fire behavior, and more to generate a detailed simulation of how this fire might spread over time.
        </div>
        <button
          style={{ 
            width: '70%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.6rem',
            fontSize: '0.8rem',
            color: 'white',
            fontWeight: '600',
            borderRadius: '8px',
            padding: '0.7rem 0',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #0080ff 0%, #0056b3 100%)',
            transition: 'all 0.2s ease',
            border: 'none'
          }}
          onClick={requestSimulation}
          onMouseEnter={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #0056b3 0%, #004085 100%)';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 8px rgba(0, 123, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'linear-gradient(135deg, #0080ff 0%, #0056b3 100%)';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          Request AI Fire Spread Simulation
        </button>
      </div>
    ) : simulation.status === 'pending' ? (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '2rem',
        textAlign: 'center',
        color: '#666',
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
        borderRadius: '8px',
        border: '1px solid #ffc107'
      }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#856404' }}>
          🔥 Simulation in Progress
        </div>
        <div style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
          Our AI is processing your fire spread simulation. This typically takes 2-5 minutes.
        </div>
        <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
          You can leave this tab and return later - we'll keep checking for updates.
        </div>
      </div>
    ) : simulation.status === 'complete' ? (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '1.5rem',
        textAlign: 'center',
        color: '#666',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        borderRadius: '8px',
        border: '1px solid #28a745',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.3rem', color: '#155724' }}>
          Simulation Ready
        </div>
        <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
          Your AI-generated fire spread simulation is complete and ready to view.
        </div>
        <button
          style={{ 
            background: 'rgba(40, 167, 69, 0.9)', 
            border: '1px solid #28a745', 
            paddingTop: '0.7rem', 
            paddingBottom: '0.7rem', 
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            fontSize: '0.8rem', 
            color: 'white', 
            fontWeight: 'bold',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)'
          }}
          onClick={() => setIsViewing(true)}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(40, 167, 69, 1)';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 8px rgba(40, 167, 69, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(40, 167, 69, 0.9)';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 4px rgba(40, 167, 69, 0.3)';
          }}
        >
          View AI Fire Spread Simulation
        </button>
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <button
          style={{ margin: 0, background: 'rgba(220, 53, 69, 0.9)', width: '100%', border: '1px solid #dc3545', paddingTop: '0.9rem', paddingBottom: '0.9rem', fontSize: '0.85rem', color: 'white', fontWeight: 'bold' }}
          className="fires-page-content-add-fire-btn"
          onClick={requestSimulation}
        >
          🔄 Retry AI Fire Spread Simulation
        </button>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '1rem',
          textAlign: 'center',
          color: '#666',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          borderRadius: '8px',
          border: '1px solid #dc3545'
        }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.3rem', color: '#721c24' }}>
            Simulation Failed
          </div>
          <div style={{ fontSize: '0.8rem' }}>
            There was an issue generating your simulation. Please try again.
          </div>
        </div>
      </div>
    )}
     {/* <video width={'100%'} style={{ marginTop: '-0.5rem' }} height="240" autoPlay loop muted>
        <source src={video2} />
        Your browser does not support the video tag.
      </video>*/}
    </div>
  )
}

return (
  <div style={{ width: '100%' }}>
    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: 'black' }}>
      AI Fire Spread Simulation
    </div>
    
    {!hasValidSimulationData ? (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '2rem',
        textAlign: 'center',
        color: '#666',
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
        borderRadius: '8px',
        border: '1px solid rgba(255, 193, 7, 0.3)'
      }}>
        <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem', color: '#856404' }}>
          ⚠️ Insufficient Data
        </div>
        <div style={{ fontSize: '0.9rem', lineHeight: '1.4', marginBottom: '1rem' }}>
          We do not have enough confident data to provide a meaningful fire spread simulation for this location. The fire area is too small or the simulation data is insufficient.
        </div>
        <div style={{ fontSize: '0.8rem', color: '#6c757d', lineHeight: '1.4' }}>
          This could be due to limited fire activity, insufficient weather data, or the fire being too small to model accurately. 
          <br/><br/>
          <strong>Note:</strong> Fire spread simulations work best in rural grassland and forest areas where fires can spread naturally. 
          In urban areas, fires typically don't spread the same way due to fire-resistant structures, roads, and fire suppression systems.
        </div>
      </div>
    ) : (
      <>
        {/* time slider */}
        <input
          type="range"
          min={0}
          max={timeSeries.length - 1}
          value={currentStep}
          onChange={e => setCurrentStep(+e.target.value)}
          style={{ width: '100%', accentColor: 'orange', color: 'white'}}
        />
    <div style={{ fontSize: '0.7rem', margin: '0.3rem 0' }}>
      {moment
        .tz(timeSeries[currentStep].time, 'UTC')
        .local().format('YYYY-MM-DD h:mm A')}
    </div>
    <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginBottom: '1rem' }}>
      <img
        src={fastForwardBtn}
        alt="<<"
        style={{ cursor: 'pointer', transform: 'rotate(180deg)', width: 25, height: 25, filter: 'invert(100%)' }}
        onClick={() => {
          setIsPlaying(false)
          setCurrentStep(prev => (prev > 0 ? prev - 1 : timeSeries.length - 1))
        }}
      />
      <img
        src={isPlaying ? pauseBtn : playBtn}
        alt="Play/Pause"
        style={{ cursor: 'pointer', width: 25, height: 25, filter: 'invert(100%)' }}
        onClick={() => setIsPlaying(p => !p)}
      />
      <img
        src={fastForwardBtn}
        alt=">>"
        style={{ cursor: 'pointer', width: 25, height: 25, filter: 'invert(100%)' }}
        onClick={() => {
          setIsPlaying(false)
          setCurrentStep(prev => (prev + 1 < timeSeries.length ? prev + 1 : 0))
        }}
      />
    </div>
        <div className="fire-popup-tabs">
          {['Nearby Infrastructure','Powerlines'].map(t => (
            <div
              key={t}
              className="fire-popup-tabs-tab"
              style={{
                width: '50%',
                borderBottom: t === stage ? '2px solid #0080ff' : '',
              }}
              onClick={() => setStage(t)}
            >
              {t}
            </div>
          ))}
        </div>

        {/* tab content */}
        {stage === 'Nearby Infrastructure' ? (
          <NearbyInfrastructure map={map} latitude={latitude} longitude={longitude} nearbyInfra={nearbyInfra} calculateInfraDistance={calculateInfraDistance} formatAddress={formatAddress} showInfraPopup={showInfraPopup}/>
        ) : (
          <Powerlines map={map} powerlines={powerlines} latitude={latitude} longitude={longitude} calculatePowerlineDistance={calculatePowerlineDistance} showPowerlinesPopup={showPowerlinesPopup} parseWKTLine={parseWKTLine}/>
        )}
      </>
    )}
  </div>
)
}
